import { z } from 'zod';

/** Statystyki postaci — działają globalnie. */
export const PLAYER_STATS = ['maxHealth', 'moveSpeed', 'pickupRadius', 'critChance', 'armor', 'regen'] as const;
/** Statystyki umiejętności — modyfikator może je zawęzić tagami (np. tylko "fire"). */
export const ABILITY_STATS = ['damage', 'cooldown', 'projectileCount', 'projectileSpeed', 'pierce', 'area', 'range'] as const;

/**
 * Statystyki wyprawy (bazowo 1): mnożniki zdobywanego złota i doświadczenia, częstości skrzyń
 * i szansy na przedmioty wyprawy.
 */
export const RUN_STATS = ['goldGain', 'xpGain', 'chestRate', 'relicFind'] as const;

/**
 * Efekty specjalne (bazowo 0), np. ze zwieńczeń drzewka i przedmiotów wyprawy:
 *  critDamage   — dodatek do mnożnika trafienia krytycznego (bazowo ×2),
 *  executeBelow — trafienie dobija wroga (nie bossa) poniżej tej części zdrowia,
 *  lifeOnKill   — zdrowie za każde zabójstwo,
 *  dodgeChance  — szansa na uniknięcie ataku wroga (pocisk, cios, wybuch),
 *  barrier      — tarcza z tej części maks. zdrowia, odnawia się chwilę po ostatnim ciosie,
 *  thorns       — obrażenia na sekundę dla wrogów, którzy dotykają bohatera,
 *  luck         — lepsze rzadkości przedmiotów wyprawy.
 */
export const EFFECT_STATS = ['critDamage', 'executeBelow', 'lifeOnKill', 'dodgeChance', 'barrier', 'thorns', 'luck'] as const;

export const StatIdSchema = z.enum([...PLAYER_STATS, ...ABILITY_STATS, ...RUN_STATS, ...EFFECT_STATS]);
export type StatId = z.infer<typeof StatIdSchema>;

const Id = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'id musi być w formacie kebab-case');
const Color = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'kolor musi być w formacie #rrggbb');

/**
 * Wartość końcowa = (baza + suma "add") * (1 + suma "mul").
 * Modyfikator z `tags` działa tylko na umiejętności mające WSZYSTKIE te tagi.
 */
export const ModifierSchema = z
  .object({
    stat: StatIdSchema,
    op: z.enum(['add', 'mul']),
    value: z.number(),
    tags: z.array(z.string()).optional(),
  })
  .strict();
export type Modifier = z.infer<typeof ModifierSchema>;

export const PlayerBaseStatsSchema = z
  .object({
    maxHealth: z.number().positive(),
    moveSpeed: z.number().positive(),
    pickupRadius: z.number().positive(),
    critChance: z.number().min(0).max(1),
    armor: z.number().min(0),
    regen: z.number().min(0),
  })
  .strict();
export type PlayerBaseStats = z.infer<typeof PlayerBaseStatsSchema>;

export const ClassSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    color: Color,
    /** Klucz modelu w kliencie (apps/game/src/three/visuals.ts). */
    visual: z.string().min(1),
    baseStats: PlayerBaseStatsSchema,
    startingAbilities: z.array(Id).min(1),
    /** Przedmioty (rzadkość zwykła) na start nowego profilu. */
    startingItems: z.array(Id).default([]),
  })
  .strict();
export type ClassDef = z.infer<typeof ClassSchema>;

export const AbilitySchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    tags: z.array(z.string()).min(1),
    /**
     * projectile — pocisk w najbliższego wroga; nova — obrażenia dookoła postaci;
     * melee — cios w łuku przed postacią, w stronę najbliższego wroga;
     * orbit — `projectileCount` ostrzy krąży wokół postaci w odległości `range` (promień ostrza `area`),
     *   co `cooldown` s rani wszystkich, których dotyka; prędkość obrotu: `projectileSpeed` (jednostki/s po okręgu);
     * chain — wyładowanie w najbliższego wroga, potem `projectileCount` przeskoków do kolejnych w promieniu `area`;
     * aura — co `cooldown` s rani wszystkich w promieniu `area` wokół postaci (bez animacji ataku);
     * trap — pułapka na ziemi przy najbliższym wrogu (albo pod postacią): uzbraja się, wybucha, gdy ktoś
     *   na nią wejdzie albo po kilku sekundach, i rani wszystkich w promieniu `area`; `projectileCount` pułapek naraz.
     */
    kind: z.enum(['projectile', 'nova', 'melee', 'orbit', 'chain', 'aura', 'trap']),
    /** Wygląd w kliencie: animacja bohatera i efekt. */
    fx: z.enum(['slash', 'cleave', 'spin', 'orb', 'bolt', 'knife', 'shard', 'flame', 'blades', 'lightning', 'aura', 'totem']),
    /**
     * Efekt na trafionym wrogu: burn — `power` obrażeń na sekundę; slow — spowolnienie o `power` (0..0,9);
     * stun — wróg stoi i nie atakuje. Bossowie są odporni na ogłuszenie, a spowolnienie działa na nich w połowie.
     */
    status: z
      .object({ kind: z.enum(['burn', 'slow', 'stun']), duration: z.number().positive(), power: z.number().min(0).default(0) })
      .strict()
      .optional(),
    damage: z.number().positive(),
    cooldown: z.number().positive(),
    /** Promień pocisku, promień novy albo zasięg ciosu wręcz. */
    area: z.number().positive(),
    /** Szerokość łuku ciosu wręcz w stopniach. */
    arc: z.number().positive().max(360).default(120),
    /** Odrzut trafionych wrogów (jednostki świata). Ujemny = przyciąganie do bohatera. */
    knockback: z.number().default(0),
    range: z.number().positive().default(400),
    projectileSpeed: z.number().positive().default(400),
    projectileCount: z.number().int().min(1).default(1),
    pierce: z.number().int().min(0).default(0),
    color: Color,
    /** Kolor na najwyższej randze — efekt przechodzi do niego stopniowo z każdym ulepszeniem. */
    rankColor: Color.optional(),
    /** Umiejętność, w którą ta ewoluuje po osiągnięciu najwyższej rangi. */
    evolution: Id.optional(),
    /** Forma po ewolucji — nie da się jej zdobyć inaczej i nie ewoluuje dalej. */
    evolved: z.boolean().default(false),
  })
  .strict();
export type AbilityDef = z.infer<typeof AbilitySchema>;

export const TalentSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    /** Puste = talent dostępny dla każdej klasy. */
    classes: z.array(Id).default([]),
    maxRank: z.number().int().min(1),
    /** Modyfikatory za JEDNĄ rangę — przy randze N wartości są mnożone przez N. */
    modifiers: z.array(ModifierSchema).default([]),
    grantsAbility: Id.optional(),
    requires: z.array(z.object({ talent: Id, rank: z.number().int().min(1) }).strict()).default([]),
  })
  .strict();
export type TalentDef = z.infer<typeof TalentSchema>;

/** Atak bossa: zapowiedź na ziemi (telegraf), po której pada cios. */
export const BossAttackSchema = z
  .object({
    /**
     * ring — pierścień pocisków dookoła, fan — wachlarz pocisków w bohatera,
     * slam — krąg pod bohaterem, cone — stożek przed bossem, charge — szarża po linii.
     */
    kind: z.enum(['ring', 'fan', 'slam', 'cone', 'charge']),
    /** Czas zapowiedzi (s) — tyle gracz ma na ucieczkę. */
    windup: z.number().min(0.3).default(1),
    /** Obrażenia ciosu (pociski ring/fan biorą obrażenia z `attack`). */
    damage: z.number().min(0).default(0),
    /** Promień kręgu (slam) albo długość stożka (cone). */
    radius: z.number().positive().default(110),
    /** Szerokość stożka w stopniach. */
    arc: z.number().positive().max(360).default(90),
    /** Długość i szerokość pasa szarży. */
    length: z.number().positive().default(360),
    width: z.number().positive().default(64),
  })
  .strict();
export type BossAttackDef = z.infer<typeof BossAttackSchema>;

export const EnemySchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    health: z.number().positive(),
    speed: z.number().positive(),
    /** Obrażenia na sekundę przy kontakcie. */
    damage: z.number().min(0),
    radius: z.number().positive(),
    xp: z.number().int().min(1),
    color: Color,
    /** Klucz modelu w kliencie (apps/game/src/three/visuals.ts). */
    visual: z.string().min(1),
    spawnWeight: z.number().positive(),
    /** Od której sekundy gry wróg może się pojawiać. */
    appearsAt: z.number().min(0),
    /** Zachowanie (engine/src/enemy-ai.ts). Wszystkie poza "chase" potrzebują `attack`. */
    behavior: z.enum(['chase', 'ranged', 'charger', 'exploder', 'boss']).default('chase'),
    attack: z
      .object({
        /** Z jakiej odległości zaczyna atak (strzał, szarżę, zapalenie lontu). */
        range: z.number().positive(),
        /** Obrażenia pocisku, uderzenia szarży albo wybuchu. */
        damage: z.number().min(0),
        /** Prędkość pocisku albo szarży. */
        speed: z.number().positive().default(260),
        /** Czas między atakami (s). */
        interval: z.number().positive(),
        /** Zamach przed atakiem (s) — czas, żeby gracz zdążył zareagować. */
        windup: z.number().min(0).default(0.6),
        /** Pocisków w salwie (u bossa: w pierścieniu). */
        count: z.number().int().min(1).default(1),
        /** Promień wybuchu. */
        radius: z.number().positive().default(60),
        shotRadius: z.number().positive().default(8),
        /** Wygląd pocisku w kliencie. */
        fx: z.enum(['orb', 'bolt', 'shard']).default('orb'),
      })
      .strict()
      .optional(),
    /** Przydomek pokazywany na banerze wejścia bossa. */
    title: z.string().optional(),
    /** Mechaniki bossa: kolejne ataki z zapowiedzią i faza szału. Brak = stara salwa (pierścień/wachlarz). */
    boss: z
      .object({
        attacks: z.array(BossAttackSchema).min(1),
        enrage: z
          .object({
            /** Próg zdrowia (0,5 = przy połowie). */
            at: z.number().min(0).max(1).default(0.5),
            /** Mnożnik prędkości ruchu w szale. */
            speed: z.number().positive().default(1.3),
            /** Mnożnik odstępu między atakami (0,75 = częściej). */
            interval: z.number().positive().default(0.75),
            /** Przywoływane sługi: kto, ilu naraz i co ile sekund. */
            summon: z.object({ enemy: z.string(), count: z.number().int().min(1), every: z.number().positive() }).strict().optional(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type EnemyDef = z.infer<typeof EnemySchema>;

export const HAZARD_KINDS = ['trucizna', 'lod', 'zar'] as const;
export type HazardKind = (typeof HAZARD_KINDS)[number];

export const ZoneSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    /** Klucz wyglądu w kliencie (teren, niebo, dekoracje). */
    visual: z.string().min(1),
    difficulty: z.number().int().min(1),
    healthMultiplier: z.number().positive(),
    rewardMultiplier: z.number().positive(),
    /** Ile przeszkód (drzew, grobów, skał) próbujemy postawić na kawałek mapy. 0 = otwarte pole. */
    obstacles: z.number().int().min(0).default(4),
    /** Wrogowie strefy i ich wagi losowania (zastępują enemy.spawnWeight). */
    enemies: z.array(z.object({ enemy: Id, weight: z.number().positive() }).strict()).min(1),
    /** Bossowie: kto i w której sekundzie. Pokonanie bossa z `final` kończy wyprawę zwycięstwem. */
    bosses: z.array(z.object({ enemy: Id, at: z.number().min(0), final: z.boolean().default(false) }).strict()).default([]),
    /** Kałuże na ziemi (trucizna, lód, żar): ranią i/lub spowalniają bohatera, który w nie wejdzie. */
    hazards: z
      .object({
        kind: z.enum(HAZARD_KINDS),
        /** Ile prób postawienia kałuży na kawałek mapy. */
        perChunk: z.number().int().min(0),
        /** Promień kałuży: od, do (jednostki świata). */
        radius: z.tuple([z.number().positive(), z.number().positive()]),
        damagePerSecond: z.number().min(0),
        /** Spowolnienie ruchu w kałuży (0.3 = o 30% wolniej). */
        slow: z.number().min(0).max(0.9),
      })
      .strict()
      .optional(),
    /** Ile obiektów do rozbicia (beczki, skrzynki, urny) próbujemy postawić na kawałek mapy. */
    breakables: z.number().int().min(0).default(0),
    /** Zalecana moc ekwipunku; przy mniejszej wrogowie są twardsi (game.power). */
    recommendedPower: z.number().min(0).default(0),
  })
  .strict();
export type ZoneDef = z.infer<typeof ZoneSchema>;

export const SLOTS = ['weapon', 'offhand', 'head', 'back', 'chest', 'legs', 'hands', 'feet'] as const;
/** Zbroja widoczna na ciele bohatera (zmienia wygląd części ciała zamiast przypinać model do kości). */
export const ARMOR_SLOTS = ['chest', 'legs', 'hands', 'feet'] as const;
export const ArmorMaterialSchema = z.enum(['metal', 'leather', 'fur', 'cloth']);
export type ArmorMaterial = z.infer<typeof ArmorMaterialSchema>;
export const SlotSchema = z.enum(SLOTS);
export type Slot = z.infer<typeof SlotSchema>;

export const RaritySchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    color: Color,
    /** Waga losowania w gachy. */
    weight: z.number().positive(),
    /** Mnożnik wartości modyfikatorów przedmiotu. */
    statMultiplier: z.number().positive(),
  })
  .strict();
export type RarityDef = z.infer<typeof RaritySchema>;

export const ItemSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    slot: SlotSchema,
    /** Klasy, które mogą to założyć (i wylosować w gachy). */
    classes: z.array(Id).min(1),
    /** Broń dwuręczna zajmuje też drugą rękę. */
    twoHanded: z.boolean().default(false),
    /**
     * Model w kliencie: "model:Węzeł" (dodatek z modelu bohatera, np. "knight:1H_Sword")
     * albo "item:id" (osobny plik z pipeline'u, zob. apps/game/src/three/item-models.json).
     */
    visual: z.string().regex(/^[a-z0-9-]+:[A-Za-z0-9_-]+$/, 'visual w formacie "model:Węzeł" albo "item:id"'),
    /** Modyfikatory dla rzadkości zwykłej, wyższe rzadkości je mnożą. */
    modifiers: z.array(ModifierSchema).min(1),
    /** Wygląd zbroi na ciele (sloty chest/legs/hands/feet): kolor i materiał, opcjonalnie kolor wykończenia. */
    look: z.object({ color: Color, material: ArmorMaterialSchema, trim: Color.optional() }).strict().optional(),
  })
  .strict()
  .refine((i) => !(ARMOR_SLOTS as readonly string[]).includes(i.slot) || !!i.look, { message: 'zbroja (chest/legs/hands/feet) musi mieć look' });
export type ItemDef = z.infer<typeof ItemSchema>;

export const GachaConfigSchema = z
  .object({
    pullCost: z.number().int().positive(),
    /** Gwarantowany epicki (lub lepszy) co tyle losowań. */
    pityEpic: z.number().int().positive(),
    /** Gwarantowany legendarny co tyle losowań. */
    pityLegendary: z.number().int().positive(),
    /** Duplikat podnosi poziom przedmiotu, do tego limitu. */
    maxLevel: z.number().int().min(1),
    /** Premia do statystyk za każdy poziom ponad pierwszy. */
    levelBonus: z.number().min(0),
    startingCurrency: z.number().int().min(0),
    currencyPerKill: z.number().min(0),
    currencyPerMinute: z.number().min(0),
    /** Duplikat przedmiotu na maksymalnym poziomie zamienia się w odłamki (rzadkość → liczba). */
    shardsPerDuplicate: z.record(z.number().int().min(0)),
    /** Koszt wzmocnienia przedmiotu odłamkami: tyle × obecny poziom. */
    shardUpgradeCost: z.number().int().positive(),
  })
  .strict();
export type GachaConfig = z.infer<typeof GachaConfigSchema>;

/**
 * Baner przywołania. Stały losuje cały sprzęt klasy; wyróżniony co jakiś czas zmienia zestaw przedmiotów,
 * które wypadają częściej, i ma sklepik za odłamki z tymi przedmiotami.
 */
export const BannerSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    /** Zestawy wyróżnionych przedmiotów, zmieniane co `rotationDays` dni (kolejno, w kółko). */
    rotation: z.array(z.array(Id).min(1)).default([]),
    rotationDays: z.number().int().positive().default(7),
    /** Szansa, że przedmiot co najmniej tej rzadkości będzie wyróżniony (jeśli klasa ma taki w zestawie). */
    featuredRate: z.number().min(0).max(1).default(0),
    featuredMinRarity: Id.optional(),
    /** Sklepik: wyróżniony przedmiot tej rzadkości za odłamki. */
    shop: z.object({ rarity: Id, cost: z.number().int().positive() }).optional(),
  })
  .strict();
export type BannerDef = z.infer<typeof BannerSchema>;

const Count = z.number().int().positive();
/** Warunek osiągnięcia — liczony z profilu (i bieżącej wyprawy, żeby dało się zdobyć je w trakcie). */
export const AchievementConditionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('kills'), count: Count }).strict(),
  z.object({ type: z.literal('runs'), count: Count }).strict(),
  z.object({ type: z.literal('boss'), enemy: Id, count: Count.default(1) }).strict(),
  z.object({ type: z.literal('survive'), zone: Id.optional(), seconds: Count }).strict(),
  /** Pokonany boss końcowy strefy; `allClasses` — każdą klasą. */
  z.object({ type: z.literal('clear'), zone: Id, allClasses: z.boolean().default(false) }).strict(),
  z.object({ type: z.literal('relics'), count: Count }).strict(),
  z.object({ type: z.literal('level'), level: Count }).strict(),
  z.object({ type: z.literal('pulls'), count: Count }).strict(),
  z.object({ type: z.literal('rarity'), rarity: Id }).strict(),
  z.object({ type: z.literal('maxLevelItem') }).strict(),
  z.object({ type: z.literal('talents'), count: Count }).strict(),
  z.object({ type: z.literal('gold'), count: Count }).strict(),
]);
export type AchievementCondition = z.infer<typeof AchievementConditionSchema>;

export const AchievementSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string().min(1),
    condition: AchievementConditionSchema,
    reward: z
      .object({
        currency: z.number().int().min(0).optional(),
        gold: z.number().int().min(0).optional(),
        /** Tytuł pod imieniem bohatera w obozie. */
        title: z.string().min(1).optional(),
        /** Ramka portretu w HUD wyprawy. */
        frame: z.object({ name: z.string().min(1), color: Color }).strict().optional(),
      })
      .strict(),
  })
  .strict();
export type AchievementDef = z.infer<typeof AchievementSchema>;

export const GameConfigSchema = z
  .object({
    gacha: GachaConfigSchema,
    playerRadius: z.number().positive(),
    talentChoices: z.number().int().min(1),
    /** Najwyższa ranga umiejętności (ulepszenia przy awansie). */
    abilityMaxRank: z.number().int().min(1),
    loot: z
      .object({
        /** Szansa na monetę ze zwykłego wroga. */
        goldDropChance: z.number().min(0).max(1),
        potionDropChance: z.number().min(0).max(1),
        /** Szansa na przedmiot wyprawy ze zwykłego / elitarnego wroga (sprzęt główny dają tylko bossowie). */
        itemDropChance: z.number().min(0).max(1),
        eliteItemChance: z.number().min(0).max(1),
        /** Wróg o takim promieniu lub większym liczy się jako elitarny. */
        eliteRadius: z.number().positive(),
        /** Część maks. zdrowia leczona przez miksturę. */
        potionHeal: z.number().min(0).max(1),
        firstChest: z.number().min(0),
        chestInterval: z.number().positive(),
        maxChests: z.number().int().min(0),
        chestDistance: z.tuple([z.number().positive(), z.number().positive()]),
        chestDespawnDistance: z.number().positive(),
        chestCoins: z.tuple([z.number().int().min(0), z.number().int().min(0)]),
        chestItemChance: z.number().min(0).max(1),
        chestPotionChance: z.number().min(0).max(1),
        /** Koszt wzmocnienia przedmiotu złotem: koszt × obecny poziom. */
        upgradeCost: z.number().int().positive(),
        /** Rozbity obiekt na mapie: szansa na monety (od, do) i na miksturę. */
        breakableGoldChance: z.number().min(0).max(1).default(0.6),
        breakableCoins: z.tuple([z.number().int().min(0), z.number().int().min(0)]).default([2, 5]),
        breakablePotionChance: z.number().min(0).max(1).default(0.12),
      })
      .strict(),
    gemMagnetSpeed: z.number().positive(),
    /** Fizyka ruchu i walki (odczucie gry). Prędkości w jednostkach świata na sekundę. */
    physics: z
      .object({
        /** Bohater: przyspieszenie przy ruszaniu i skręcie oraz hamowanie po puszczeniu sterowania. */
        playerAccel: z.number().positive(),
        playerDecel: z.number().positive(),
        /** Odrzut: wytłumienie prędkości (1/s). Odrzut przelatuje tę samą drogę co dawniej, ale płynnie. */
        knockbackDamping: z.number().positive(),
        /** Odrzucony wróg traci kontrolę nad ruchem, dopóki prędkość odrzutu przekracza jego prędkość × ten mnożnik. */
        staggerRatio: z.number().min(0),
        /** Siła rozpychania się wrogów (× ich prędkość); ciężki wróg słabo ustępuje lekkiemu. */
        separation: z.number().min(0),
        /** Kryształy i łupy rozsypują się z prędkością (od, do) i hamują z tym tłumieniem (1/s). */
        lootScatter: z.tuple([z.number().min(0), z.number().min(0)]),
        lootFriction: z.number().positive(),
        /** Magnes: łup startuje z tą prędkością i przyspiesza do gemMagnetSpeed. */
        magnetStartSpeed: z.number().min(0),
        magnetAccel: z.number().positive(),
        /**
         * Pociski rozbijają się o drzewa, groby i skały: osobno bohatera (zwykle nie — celują same) i wrogów
         * (można się schować; domyślnie wyłączone, bo w symulacji wyraźnie wzmacniało klasy dystansowe).
         */
        playerShotsHitObstacles: z.boolean(),
        hostileShotsHitObstacles: z.boolean(),
        /** Odstęp, który wrogowie trzymają między sobą (× suma promieni) — modele są szersze niż koło kolizji. */
        personalSpace: z.number().min(1),
        /** Ile nakładania się wrogów usuwać na klatkę (0..1) — tłum zostaje tłumem, a nie jedną kulą. */
        crowdCorrection: z.number().min(0).max(1),
        /** Najwyższa prędkość odrzutu — kilka trafień naraz nie wystrzeli wroga przez pół mapy. */
        maxKnockbackSpeed: z.number().positive(),
      })
      .strict()
      .default({
        playerAccel: 2400,
        playerDecel: 2000,
        knockbackDamping: 10,
        staggerRatio: 1,
        separation: 1.6,
        lootScatter: [45, 90],
        lootFriction: 7,
        magnetStartSpeed: 120,
        magnetAccel: 1400,
        playerShotsHitObstacles: false,
        hostileShotsHitObstacles: false,
        personalSpace: 1.15,
        crowdCorrection: 0.3,
        maxKnockbackSpeed: 540,
      }),
    enemyHealthScalingPerMinute: z.number().min(0),
    xp: z.object({ base: z.number().positive(), growth: z.number().min(1) }).strict(),
    /** Kara za za słaby sprzęt: +zdrowie wrogów = brakująca część mocy × penaltyPerMissing, najwyżej maxPenalty. */
    power: z
      .object({ penaltyPerMissing: z.number().min(0), maxPenalty: z.number().min(0) })
      .strict()
      .default({ penaltyPerMissing: 0.6, maxPenalty: 0.5 }),
    /** Wydarzenia w trakcie wyprawy (mgła, przeklęta skrzynia, krwawy księżyc). */
    runEvents: z
      .object({
        /** Pierwsze wydarzenie po tylu sekundach, kolejne co (od, do) sekund. */
        first: z.number().min(0),
        interval: z.tuple([z.number().positive(), z.number().positive()]),
        list: z
          .array(
            z
              .object({
                id: Id,
                name: z.string().min(1),
                description: z.string(),
                /** fog — mgła (gorsza widoczność), cursedChest — skrzynia ze strażą, frenzy — wrogowie napływają szybciej. */
                kind: z.enum(['fog', 'cursedChest', 'frenzy']),
                duration: z.number().positive(),
                /** Przeklęta skrzynia: ilu strażników. */
                guards: z.number().int().min(0).default(0),
                /** Krwawy księżyc: o ile szybciej pojawiają się wrogowie (0,6 = +60%). */
                spawnBoost: z.number().min(0).default(0),
              })
              .strict(),
          )
          .min(1),
      })
      .strict()
      .optional(),
    spawn: z
      .object({
        baseInterval: z.number().positive(),
        minInterval: z.number().positive(),
        rampSeconds: z.number().positive(),
        groupGrowthSeconds: z.number().positive(),
        ringDistance: z.number().positive(),
        maxEnemies: z.number().int().positive(),
      })
      .strict(),
  })
  .strict();
export type GameConfig = z.infer<typeof GameConfigSchema>;

/** Gałęzie drzewka talentów w obozie. „klasa” ma osobne węzły dla każdej klasy (pole `classes`). */
export const UPGRADE_BRANCHES = ['atak', 'obrona', 'fortuna', 'klasa'] as const;

/**
 * Talent w obozie (drzewko rozwoju), kupowany złotem i działający na każdej wyprawie.
 * Położenie: gałąź, rząd (od góry) i kolumna (0–2). `requires` = ile punktów trzeba wydać w tej gałęzi,
 * `after` = talent, który musi mieć choć jedną rangę (linia na drzewku).
 */
export const UpgradeSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    branch: z.enum(UPGRADE_BRANCHES),
    row: z.number().int().min(0),
    col: z.number().int().min(0).max(2),
    requires: z.number().int().min(0).default(0),
    after: Id.optional(),
    maxRank: z.number().int().min(1),
    /** Koszt rangi N (od 0): base × growth^N, zaokrąglony. */
    cost: z.object({ base: z.number().int().positive(), growth: z.number().min(1) }).strict(),
    /** Modyfikatory za JEDNĄ rangę. */
    modifiers: z.array(ModifierSchema).min(1),
    /** Tylko dla tych klas (wymagane w gałęzi „klasa”). */
    classes: z.array(Id).optional(),
    /** Zwieńczenie gałęzi: większy węzeł z unikalnym efektem. */
    capstone: z.boolean().default(false),
    /** Ikona: klucz istniejącej ikony, np. "stat:critChance" albo "upgrade:sila" (domyślnie ikona statystyki). */
    icon: z.string().regex(/^(stat|upgrade):[A-Za-z0-9-]+$/).optional(),
  })
  .strict();
export type UpgradeDef = z.infer<typeof UpgradeSchema>;

/** Rodzaje przedmiotów wyprawy (działają tylko do końca wyprawy, nie trafiają do ekwipunku). */
export const RELIC_KINDS = ['pierscien', 'amulet', 'talizman'] as const;

/** Przedmiot wyprawy: znaleziony w skrzyni albo przy wrogu, działa od razu i przepada po powrocie do obozu. */
export const RelicSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    kind: z.enum(RELIC_KINDS),
    /** Rzadkość (id z rarities.json) — rzadsze wypadają rzadziej i są silniejsze. */
    rarity: Id,
    /** Kolor kamienia na ikonie. */
    color: Color,
    modifiers: z.array(ModifierSchema).min(1),
  })
  .strict();
export type RelicDef = z.infer<typeof RelicSchema>;

/**
 * Rasa bohatera: cechy rasowe (opis do UI), stałe modyfikatory i zdolności okresowe
 * (co `every` sekund działa przez `duration` sekund). Wygląd zależy od rasy i płci (modele w kliencie).
 */
export const RaceSchema = z
  .object({
    id: Id,
    name: z.string().min(1),
    description: z.string(),
    traits: z.array(z.object({ name: z.string().min(1), description: z.string() }).strict()).min(1),
    modifiers: z.array(ModifierSchema).default([]),
    periodic: z
      .array(
        z
          .object({
            name: z.string().min(1),
            every: z.number().positive(),
            duration: z.number().positive(),
            modifiers: z.array(ModifierSchema).min(1),
          })
          .strict(),
      )
      .default([]),
    /**
     * Wygląd gołego ciała rasy: kolor skóry, włosy, proporcje [szerokość, wysokość] względem człowieka.
     * Płeć „k” dostaje własne kolory/proporcje, jeśli podane.
     */
    look: z
      .object({
        skinTone: Color,
        hair: Color,
        build: z.tuple([z.number().positive(), z.number().positive()]),
        female: z.object({ hair: Color.optional(), build: z.tuple([z.number().positive(), z.number().positive()]).optional() }).strict().default({}),
      })
      .strict(),
  })
  .strict();
export type RaceDef = z.infer<typeof RaceSchema>;
